// deps
import * as React from "react";

// internal deps
import { PaymentMethod, PaymentMethodMeta, isAmazonPayLater, getAmazonPaySdkParams } from "@swiggy-private/payment-ui";
import { useMountedRef, captureError } from "@swiggy-private/core-ui";
import { Some } from "@swiggy-private/js-fp";
import { AnalyticsContext, GroupOrderCartContext, PlaceOrderData } from "@swiggy-private/checkout-core";
import { OnPayExtraParams, PAYMENT_CODES } from "@swiggy-private/payment-types";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { PlaceOrderContext } from "@payment-client/contexts/PlaceOrderContext";
import { SplitPayListContext } from "@payment-client/contexts/SplitPayListContext";

// constants
import { PLACE_ORDER_SOURCE } from "@swiggy-private/payment-types";
import { PLACE_ORDER_EXTRA_PARAMS } from "@payment-client/constants/Order";

// helpers
import {
    getFrictionLayoutId,
    getSplitPayAmountsObj,
    getTotalSplitPayAmountUsed,
    getTwidRewardsMetaObject,
} from "@payment-client/helpers/PaymentHelper";
import { isEmpty } from "@swiggy-private/core-ui";

// analytics
import { onPayButtonClickEvent } from "@payment-client/analytics/events/Payment";
import { isBusinessLineRegular } from "@payment-client/helpers/Misc";

// amazon pay
import { useAmazonPayHandler } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UseAmazonPayHandler";

export type OnPay = (
    paymentMethodData: PaymentMethod<PaymentMethodMeta>,
    extraParams?: OnPayExtraParams,
    extraArbitaryData?: PlaceOrderData["extraArbitaryData"],
) => void;

export const useOnPay = (): OnPay => {
    const isMountedRef = useMountedRef();
    const { paymentResponse } = React.useContext(ShowPaymentContext);
    const { withSplitPay, withCoupon, billTotal, selectedSplitPayMethods, paymentPageOfferEnabled } =
        React.useContext(PaymentPayContext);
    const { state: groupCartData } = React.useContext(GroupOrderCartContext);
    const { processingOrder, doPlaceOrder } = React.useContext(PlaceOrderContext);
    const { clickEvent } = React.useContext(AnalyticsContext);
    const { splitPayList } = React.useContext(SplitPayListContext);

    const { getAmazonPaySdkStatus, getAmazonPayMetaId } = useAmazonPayHandler(Number(billTotal));
    const { state: amazonPaySdkState } = getAmazonPaySdkStatus();

    const selectedSplitPayAmountObj = React.useMemo(
        () => getSplitPayAmountsObj(withSplitPay, selectedSplitPayMethods, splitPayList),
        [withSplitPay, selectedSplitPayMethods, splitPayList],
    );

    const twidRewardsObject = React.useMemo(
        () => getTwidRewardsMetaObject(withSplitPay, selectedSplitPayMethods, splitPayList),
        [withSplitPay, selectedSplitPayMethods, splitPayList],
    );

    const removeReward = (selectedSplitPayAmountObj: { [key: string]: string }) => {
        delete selectedSplitPayAmountObj[PAYMENT_CODES.TWID_REWARD];
        return selectedSplitPayAmountObj;
    };

    const getAplExtraParams = React.useCallback(
        async (): Promise<OnPayExtraParams> => {
            let metaId = "";
            try {
                metaId = (await getAmazonPayMetaId()) ?? "";
            } catch (err) {
                captureError(err);
            }
            const isCreditLimitSufficient = amazonPaySdkState?.apl?.isCreditLimitSufficient ?? false;
            return getAmazonPaySdkParams(isCreditLimitSufficient, metaId);
        },
        [getAmazonPayMetaId, amazonPaySdkState],
    );

    const onPay = React.useCallback<OnPay>(
        async (paymentMethodData, params, extraArbitaryData): Promise<void> => {
            if (!isMountedRef.current || processingOrder) {
                return;
            }

            let forceRemoveRewards = false;

            // in case user selected a twid reward in splitpay and adds a new card on which reward is not applicable
            // then we have to remove twid rewards from splitpay during place order call
            if (
                !isEmpty(extraArbitaryData) &&
                // eslint-disable-next-line no-prototype-builtins
                extraArbitaryData.hasOwnProperty("isRewardApplicable") &&
                !extraArbitaryData.isRewardApplicable
            ) {
                forceRemoveRewards = true;
            }

            const _selectedSplitPayAmountObj = forceRemoveRewards
                ? removeReward({ ...selectedSplitPayAmountObj })
                : selectedSplitPayAmountObj;
            const totalSplitPayAmountUsed = getTotalSplitPayAmountUsed(_selectedSplitPayAmountObj);

            //Currently we compare cart amount to pricing amount in place/confirm order, we will now start comparing transaction amount to pricing amount. Transaction amount is total amount user pays including SM.
            let transactionAmount = billTotal;
            //used paymentMethodData.payableAmount because every payment method might have a different amount
            if (!isEmpty(paymentMethodData.payableAmount) && (paymentPageOfferEnabled || params?.isEmi)) {
                transactionAmount = (Number(totalSplitPayAmountUsed) || 0) + Number(paymentMethodData.payableAmount);
            }

            const placeOrderSource: OnPayExtraParams = isBusinessLineRegular()
                ? {
                      /* eslint-disable @typescript-eslint/naming-convention */
                      place_order_source: extraArbitaryData?.isTurboPaymentMethod
                          ? PLACE_ORDER_SOURCE.TURBOCHECKOUT
                          : PLACE_ORDER_SOURCE.PAYMENT,
                  }
                : {};

            const aplParams = isAmazonPayLater(paymentMethodData.paymentCode) ? await getAplExtraParams() : {};

            const extraParams: OnPayExtraParams = {
                force_validate_coupon: withCoupon,
                transaction_amount: transactionAmount,
                [PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT]:
                    selectedSplitPayAmountObj[PAYMENT_CODES.SWIGGYPAY] != null
                        ? selectedSplitPayAmountObj[PAYMENT_CODES.SWIGGYPAY]
                        : undefined,
                selected_split_pay_amounts: _selectedSplitPayAmountObj,
                twid_rewards_meta: forceRemoveRewards ? {} : twidRewardsObject,
                /* eslint-enable @typescript-eslint/naming-convention */
                ...placeOrderSource,
                ...Some(params).unwrapOr({}),
                ...aplParams,
            };

            //click event credPay
            const frictionLayoutId = getFrictionLayoutId(paymentResponse);
            onPayButtonClickEvent(
                {
                    paymentMethodData,
                    frictionLayoutId,
                    isTurboPaymentMethod: extraArbitaryData?.isTurboPaymentMethod,
                    isTurboV2: extraArbitaryData?.isTurboV2,
                    slidePercent: extraArbitaryData?.slidePercent,
                    isPaymentMethodFacingDowntime: extraArbitaryData?.isPaymentMethodFacingDowntime,
                    groupCartId: groupCartData?.data?.group_cart_id,
                    transactionAmount,
                },
                clickEvent,
            );

            doPlaceOrder({ paymentMethodData, extraParams, extraArbitaryData });
        },
        [
            isMountedRef,
            processingOrder,
            billTotal,
            paymentPageOfferEnabled,
            withCoupon,
            selectedSplitPayAmountObj,
            paymentResponse,
            groupCartData?.data?.group_cart_id,
            clickEvent,
            doPlaceOrder,
            twidRewardsObject,
            getAplExtraParams,
        ],
    );

    return onPay;
};
